import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faLinkedin,
  faFacebook,
  faInstagram,
  faXTwitter,
  faYoutube,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
  faBluesky,
} from '@fortawesome/free-brands-svg-icons';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { Icon } from '@/components/ui/Icon';
import { HoverCard } from '@/components/ui/HoverCard';
import { DashboardPostPopover } from './DashboardPostPopover';
import { ICONS } from '@/constants/icons';
import { useSomethingThatWorked } from '@/hooks/useDashboardStats';
import { useTimezone } from '@/contexts/TimezoneContext';
import { formatCompactNumber } from '@/utils/formatNumber';

/**
 * Something Worked Section - Top-performing posts based on engagement rate.
 * Shows a hero card for #1 and compact list rows for #2–5 with HoverCard popovers.
 */

// FontAwesome platform icons (consistent with PublishedSection)
const PLATFORM_FA_ICONS: Record<string, IconDefinition> = {
  LINKEDIN: faLinkedin,
  FACEBOOK: faFacebook,
  INSTAGRAM: faInstagram,
  TWITTER: faXTwitter,
  YOUTUBE: faYoutube,
  TIKTOK: faTiktok,
  PINTEREST: faPinterest,
  THREADS: faThreads,
  GOOGLE_MY_BUSINESS: faGoogle,
  BLUESKY: faBluesky,
};

// Platform colors for icons
const PLATFORM_COLORS: Record<string, string> = {
  LINKEDIN: '#0A66C2',
  FACEBOOK: '#1877F2',
  INSTAGRAM: '#E4405F',
  TWITTER: '#000000',
  YOUTUBE: '#FF0000',
  TIKTOK: '#000000',
  PINTEREST: '#E60023',
  THREADS: '#000000',
  GOOGLE_MY_BUSINESS: '#4285F4',
  BLUESKY: '#0085FF',
};

/** Derives the short performance badge text for a post. */
function getPerformanceBadge(multiplier: number): string {
  if (multiplier >= 2) return `${multiplier.toFixed(1)}× vs avg`;
  const pct = Math.round((multiplier - 1) * 100);
  return pct >= 0 ? `+${pct}% vs avg` : `${pct}% vs avg`;
}

export const SomethingWorkedSection = () => {
  const { t } = useTranslation('dashboard');
  const { formatRelativeDate, formatDateTime } = useTimezone();
  const navigate = useNavigate();
  const { data, isLoading } = useSomethingThatWorked();

  // Hide entirely when loading or no data
  if (isLoading || !data?.topPerformers?.length) {
    return null;
  }

  const [hero, ...rest] = data.topPerformers;
  const compactPosts = rest.slice(0, 4); // Show max 5 total (1 hero + 4 compact)

  return (
    <div className="flex flex-col gap-2 h-full overflow-y-auto">
      {/* Hero card — post #1 (with popover) */}
      <HoverCard
        trigger={
          <div
            className="bg-white rounded-lg border border-gray-100 p-2.5 cursor-pointer hover:border-primary-200 transition-colors"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(`/analytics?post=${hero.channelInstanceId}`); }}
          >
            <div className="flex items-center gap-2.5">
              {/* Channel profile photo with platform badge */}
              <div className="relative flex-shrink-0">
                {hero.channelAvatarUrl ? (
                  <img src={hero.channelAvatarUrl} alt="" className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <div className="w-9 h-9 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
                    <Icon name={ICONS.IMAGE} size="sm" color="text-gray-400" />
                  </div>
                )}
                <span
                  className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full flex items-center justify-center border-[1.5px] border-white"
                  style={{ backgroundColor: PLATFORM_COLORS[hero.platform] || '#6B7280' }}
                >
                  <FontAwesomeIcon icon={PLATFORM_FA_ICONS[hero.platform] || faGlobe} className="text-white text-[6px]" aria-hidden="true" />
                </span>
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-900 line-clamp-1">
                  {hero.postCaption || t('common:no_caption', { defaultValue: 'No caption' })}
                </p>
                <div className="flex items-center gap-1 mt-0.5 text-[11px] text-gray-500">
                  <span className="truncate">{hero.platformUsername}</span>
                  <span className="text-gray-300">·</span>
                  <span className="whitespace-nowrap">{formatRelativeDate(hero.publishedAt)}</span>
                </div>
              </div>

              <span className="flex-shrink-0 px-2 py-0.5 bg-primary-500 text-primary-contrast text-[10px] font-semibold rounded-full whitespace-nowrap">
                {getPerformanceBadge(hero.performanceMultiplier)}
              </span>
            </div>

            {/* Stats row */}
            <div className="flex items-center gap-3 mt-2 pt-1.5 border-t border-gray-50 text-[11px]">
              <span className="text-primary-600 font-medium">
                {formatCompactNumber(hero.reach)} reach
              </span>
              <span className="text-primary-600 font-medium">
                {formatCompactNumber(hero.engagement)} engmt
              </span>
            </div>
          </div>
        }
        side="left"
        align="start"
        openDelay={150}
      >
        <DashboardPostPopover
          gradient="from-primary-500 to-primary-600"
          headerIcon={ICONS.SPARKLES}
          headerLabel={t('dashboard_something_worked_top_performer', { defaultValue: 'Top Performer #1' })}
          headerLines={[{ icon: ICONS.CALENDAR, text: formatDateTime(hero.publishedAt) }]}
          postMedia={hero.postMedia || []}
          caption={hero.postCaption}
          footer={
            <button
              onClick={() => navigate(`/analytics?post=${hero.channelInstanceId}`)}
              className="w-full text-center py-2.5 bg-primary-500 text-primary-contrast text-xs font-semibold rounded-lg hover:bg-primary-600 transition-colors"
            >
              {t('dashboard_something_worked_view_analytics', { defaultValue: 'View in Analytics' })}
            </button>
          }
        >
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 mb-0.5">{t('dashboard_something_worked_reach_label', { defaultValue: 'Reach' })}</div>
              <div className="text-sm font-bold text-gray-900">{formatCompactNumber(hero.reach)}</div>
            </div>
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 mb-0.5">{t('dashboard_something_worked_engagement_label', { defaultValue: 'Engagement' })}</div>
              <div className="text-sm font-bold text-gray-900">{formatCompactNumber(hero.engagement)}</div>
            </div>
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 mb-0.5">{t('dashboard_something_worked_likes_label', { defaultValue: 'Likes' })}</div>
              <div className="text-sm font-bold text-gray-900">{formatCompactNumber(hero.likes)}</div>
            </div>
            <div className="text-center p-2 bg-gray-50 rounded-lg">
              <div className="text-xs text-gray-500 mb-0.5">{t('dashboard_something_worked_comments_label', { defaultValue: 'Comments' })}</div>
              <div className="text-sm font-bold text-gray-900">{formatCompactNumber(hero.comments)}</div>
            </div>
            {hero.shares > 0 && (
              <div className="text-center p-2 bg-gray-50 rounded-lg">
                <div className="text-xs text-gray-500 mb-0.5">{t('dashboard_something_worked_shares_label', { defaultValue: 'Shares' })}</div>
                <div className="text-sm font-bold text-gray-900">{formatCompactNumber(hero.shares)}</div>
              </div>
            )}
            {hero.saves > 0 && (
              <div className="text-center p-2 bg-gray-50 rounded-lg">
                <div className="text-xs text-gray-500 mb-0.5">{t('dashboard_something_worked_saves_label', { defaultValue: 'Saves' })}</div>
                <div className="text-sm font-bold text-gray-900">{formatCompactNumber(hero.saves)}</div>
              </div>
            )}
          </div>
          <div className="mt-3 p-2 bg-primary-50 rounded-lg text-center">
            <span className="text-xs font-semibold text-primary-700">{getPerformanceBadge(hero.performanceMultiplier)}</span>
            <span className="text-xs text-primary-700 font-medium ml-1">
              {t('dashboard_something_worked_er_rate', { rate: hero.engagementRate.toFixed(1), defaultValue: '· {{rate}}% ER' })}
            </span>
          </div>
        </DashboardPostPopover>
      </HoverCard>

      {/* Compact list — posts #2–3 */}
      {compactPosts.length > 0 && (
        <div className="flex flex-col gap-1">
          {compactPosts.map((post, index) => {
            const rank = index + 2; // #2, #3
            const badgeText = getPerformanceBadge(post.performanceMultiplier);

            return (
              <HoverCard
                key={post.channelInstanceId}
                trigger={
                  <div
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/70 transition-colors cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate(`/analytics?post=${post.channelInstanceId}`);
                      }
                    }}
                    aria-label={`Post #${rank}: ${post.postCaption || 'No caption'} — ${badgeText}`}
                  >
                    {/* Rank number */}
                    <span className="w-5 text-center text-xs font-bold text-gray-400 flex-shrink-0">
                      #{rank}
                    </span>

                    {/* Channel avatar with platform badge */}
                    <div className="relative flex-shrink-0">
                      {post.channelAvatarUrl ? (
                        <img src={post.channelAvatarUrl} alt="" className="w-5 h-5 rounded-full object-cover" />
                      ) : (
                        <span
                          className="w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: PLATFORM_COLORS[post.platform] || '#6B7280' }}
                        >
                          <FontAwesomeIcon
                            icon={PLATFORM_FA_ICONS[post.platform] || faGlobe}
                            className="text-white text-[8px]"
                            aria-hidden="true"
                          />
                        </span>
                      )}
                      {post.channelAvatarUrl && (
                        <span
                          className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full flex items-center justify-center border-[1px] border-white"
                          style={{ backgroundColor: PLATFORM_COLORS[post.platform] || '#6B7280' }}
                        >
                          <FontAwesomeIcon
                            icon={PLATFORM_FA_ICONS[post.platform] || faGlobe}
                            className="text-white text-[5px]"
                            aria-hidden="true"
                          />
                        </span>
                      )}
                    </div>

                    {/* Caption */}
                    <span className="flex-1 min-w-0 text-xs text-gray-700 truncate">
                      {post.postCaption || t('common:no_caption', { defaultValue: 'No caption' })}
                    </span>

                    {/* Performance badge */}
                    <span className="flex-shrink-0 px-1.5 py-0.5 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full whitespace-nowrap">
                      {badgeText}
                    </span>
                  </div>
                }
                side="left"
                align="start"
                openDelay={150}
              >
                <DashboardPostPopover
                  gradient="from-primary-500 to-primary-600"
                  headerIcon={ICONS.SPARKLES}
                  headerLabel={t('dashboard_something_worked_top_performer', {
                    defaultValue: 'Top Performer',
                  })}
                  headerLines={[
                    {
                      icon: ICONS.CALENDAR,
                      text: formatDateTime(post.publishedAt),
                    },
                  ]}
                  postMedia={post.postMedia || []}
                  caption={post.postCaption}
                  footer={
                    <button
                      onClick={() => navigate(`/analytics?post=${post.channelInstanceId}`)}
                      className="w-full text-center py-2.5 bg-primary-500 text-primary-contrast text-xs font-semibold rounded-lg hover:bg-primary-600 transition-colors"
                    >
                      {t('dashboard_something_worked_view_analytics', {
                        defaultValue: 'View in Analytics',
                      })}
                    </button>
                  }
                >
                  {/* Metrics grid */}
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <div className="text-center p-2 bg-gray-50 rounded-lg">
                      <div className="text-xs text-gray-500 mb-0.5">
                        {t('dashboard_something_worked_reach_label', { defaultValue: 'Reach' })}
                      </div>
                      <div className="text-sm font-bold text-gray-900">
                        {formatCompactNumber(post.reach)}
                      </div>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded-lg">
                      <div className="text-xs text-gray-500 mb-0.5">
                        {t('dashboard_something_worked_engagement_label', {
                          defaultValue: 'Engagement',
                        })}
                      </div>
                      <div className="text-sm font-bold text-gray-900">
                        {formatCompactNumber(post.engagement)}
                      </div>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded-lg">
                      <div className="text-xs text-gray-500 mb-0.5">
                        {t('dashboard_something_worked_likes_label', { defaultValue: 'Likes' })}
                      </div>
                      <div className="text-sm font-bold text-gray-900">
                        {formatCompactNumber(post.likes)}
                      </div>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded-lg">
                      <div className="text-xs text-gray-500 mb-0.5">
                        {t('dashboard_something_worked_comments_label', {
                          defaultValue: 'Comments',
                        })}
                      </div>
                      <div className="text-sm font-bold text-gray-900">
                        {formatCompactNumber(post.comments)}
                      </div>
                    </div>
                    {post.shares > 0 && (
                      <div className="text-center p-2 bg-gray-50 rounded-lg">
                        <div className="text-xs text-gray-500 mb-0.5">
                          {t('dashboard_something_worked_shares_label', { defaultValue: 'Shares' })}
                        </div>
                        <div className="text-sm font-bold text-gray-900">
                          {formatCompactNumber(post.shares)}
                        </div>
                      </div>
                    )}
                    {post.saves > 0 && (
                      <div className="text-center p-2 bg-gray-50 rounded-lg">
                        <div className="text-xs text-gray-500 mb-0.5">
                          {t('dashboard_something_worked_saves_label', { defaultValue: 'Saves' })}
                        </div>
                        <div className="text-sm font-bold text-gray-900">
                          {formatCompactNumber(post.saves)}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Performance summary */}
                  <div className="mt-3 p-2 bg-primary-50 rounded-lg text-center">
                    <span className="text-xs font-semibold text-primary-700">{badgeText}</span>
                    <span className="text-xs text-primary-700 font-medium ml-1">
                      {t('dashboard_something_worked_er_rate', {
                        rate: post.engagementRate.toFixed(1),
                        defaultValue: '· {{rate}}% ER',
                      })}
                    </span>
                  </div>
                </DashboardPostPopover>
              </HoverCard>
            );
          })}
        </div>
      )}
    </div>
  );
};
